/* eslint-disable react/jsx-one-expression-per-line */
import { Form } from 'antd';
import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';

import Input from '@/atoms/input';
import PhoneInput from '@/atoms/phone-input';

interface PersonalInfoFormProps {
  handlePersonalInfoInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  personalInfo: any;
  form: any;
  compact?: boolean;
  noFormWrapper?: boolean;
}

const PersonalInfoForm: FC<PersonalInfoFormProps> = ({
  handlePersonalInfoInputChange,
  personalInfo,
  form,
  compact,
  noFormWrapper,
}) => {
  const { t } = useTranslation();
  if (compact) {
    const formItems = (
      <>
        {/* Row 1: Name + Email */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-3">
          <Form.Item
            initialValue={personalInfo.name}
            name="name"
            label={t('personalInfo.fullName')}
            rules={[{ required: true, message: t('personalInfo.validation.nameRequired') }]}
            style={{ marginBottom: 12 }}
          >
            <Input
              value={personalInfo.name}
              name="name"
              onChange={handlePersonalInfoInputChange}
              placeholder={t('personalInfo.fullName')}
            />
          </Form.Item>

          <Form.Item
            initialValue={personalInfo.email}
            name="email"
            label={t('personalInfo.email')}
            rules={[
              { required: true, message: t('personalInfo.validation.emailRequired') },
              { type: 'email', message: t('personalInfo.validation.emailInvalid') },
            ]}
            style={{ marginBottom: 12 }}
          >
            <Input name="email" onChange={handlePersonalInfoInputChange} placeholder={t('personalInfo.email')} />
          </Form.Item>
        </div>

        {/* Row 2: Company + EIN (narrower) + Phone (wider) */}
        <div className="flex flex-col md:flex-row gap-x-3">
          <div className="min-w-0" style={{ flex: '1 1 0%' }}>
            <Form.Item
              initialValue={personalInfo.company}
              name="company"
              label={t('personalInfo.companyName')}
              rules={[{ required: true, message: t('personalInfo.validation.companyRequired') }]}
              style={{ marginBottom: 12 }}
            >
              <Input
                name="company"
                onChange={handlePersonalInfoInputChange}
                placeholder={t('personalInfo.companyName')}
              />
            </Form.Item>
          </div>

          <div className="min-w-0" style={{ flex: '0.6 1 0%' }}>
            <Form.Item
              initialValue={personalInfo.ein}
              name="ein"
              label={t('personalInfo.ein')}
              style={{ marginBottom: 12 }}
            >
              <Input name="ein" onChange={handlePersonalInfoInputChange} placeholder={t('personalInfo.ein')} />
            </Form.Item>
          </div>

          <div className="min-w-0" style={{ flex: '1.4 1 0%' }}>
            <Form.Item
              initialValue={{ code: personalInfo.phoneCode, phone: personalInfo.phone, short: 'US' }}
              label={t('personalInfo.phoneNumber')}
              name="phone"
              rules={[{ required: true, message: t('personalInfo.validation.phoneRequired') }]}
              style={{ marginBottom: 12 }}
            >
              <PhoneInput
                name="phone"
                value={{ code: personalInfo.phoneCode, phone: personalInfo.phone, short: 'US' }}
                onChange={(value) => {
                  handlePersonalInfoInputChange({ target: { name: 'phoneCode', value: value.code } } as any);
                  handlePersonalInfoInputChange({ target: { name: 'phone', value: value.phone } } as any);
                }}
              />
            </Form.Item>
          </div>
        </div>
      </>
    );

    if (noFormWrapper) {
      return <div className="w-full">{formItems}</div>;
    }

    return (
      <div className="w-full">
        <Form initialValues={{ ...PersonalInfoForm }} form={form} layout="vertical" className="w-full">
          {formItems}
        </Form>
      </div>
    );
  }

  // Original layout for day pass and packages
  return (
    <div className="flex flex-wrap p-0 md:p-6 w-full justify-center">
      <Form initialValues={{ ...PersonalInfoForm }} form={form} className="w-full flex flex-wrap p-5 md:p-6">
        <div className="w-full">
          <Form.Item
            initialValue={personalInfo.name}
            labelCol={{ span: 5 }}
            wrapperCol={{ span: 25 }}
            style={{ textAlign: 'start' }}
            name="name"
            label={t('personalInfo.fullName')}
            rules={[{ required: true, message: t('personalInfo.validation.nameRequired') }]}
          >
            <Input
              value={personalInfo.name}
              name="name"
              onChange={handlePersonalInfoInputChange}
              placeholder={t('personalInfo.fullName')}
            />
          </Form.Item>
        </div>

        <div className="w-full">
          <Form.Item
            initialValue={personalInfo.email}
            labelCol={{ span: 5 }}
            wrapperCol={{ span: 25 }}
            style={{ textAlign: 'start' }}
            name="email"
            label={t('personalInfo.email')}
            rules={[
              { required: true, message: t('personalInfo.validation.emailRequired') },
              { type: 'email', message: t('personalInfo.validation.emailInvalid') },
            ]}
          >
            <Input name="email" onChange={handlePersonalInfoInputChange} placeholder={t('personalInfo.email')} />
          </Form.Item>
        </div>
        <div className="w-full">
          <Form.Item
            initialValue={personalInfo.company}
            labelCol={{ span: 5 }}
            wrapperCol={{ span: 25 }}
            style={{ textAlign: 'start' }}
            name="company"
            label={t('personalInfo.companyName')}
            rules={[{ required: true, message: t('personalInfo.validation.companyRequired') }]}
          >
            <Input
              name="company"
              onChange={handlePersonalInfoInputChange}
              placeholder={t('personalInfo.companyName')}
            />
          </Form.Item>
        </div>
        <div className="w-full">
          <Form.Item
            initialValue={{ code: personalInfo.phoneCode, phone: personalInfo.phone, short: 'US' }}
            labelCol={{ span: 5 }}
            wrapperCol={{ span: 25 }}
            label={t('personalInfo.phoneNumber')}
            name="phone"
            rules={[{ required: true, message: t('personalInfo.validation.phoneRequired') }]}
          >
            <PhoneInput
              name="phone"
              value={{ code: personalInfo.phoneCode, phone: personalInfo.phone, short: 'US' }}
              onChange={(value) => {
                handlePersonalInfoInputChange({ target: { name: 'phoneCode', value: value.code } } as any);
                handlePersonalInfoInputChange({ target: { name: 'phone', value: value.phone } } as any);
              }}
            />
          </Form.Item>
        </div>
        <div className="w-full">
          <Form.Item
            initialValue={personalInfo.ein}
            labelCol={{ span: 5 }}
            wrapperCol={{ span: 25 }}
            style={{ textAlign: 'start' }}
            name="ein"
            label={t('personalInfo.ein')}
          >
            <Input name="ein" onChange={handlePersonalInfoInputChange} placeholder={t('personalInfo.ein')} />
          </Form.Item>
        </div>
      </Form>
    </div>
  );
};

export default PersonalInfoForm;
